import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { catchError, forkJoin, map, Observable } from 'rxjs';
import { Fluid } from 'src/app/perical/models/fluid.model';
import {
  CementingOperation,
  CirculationOperation,
  DrillingOperation,
  GasLiftOperation,
  InjectionOperation,
  MudPit,
  OperationType,
  ProductionOperation,
  RunCasingAndCirculate,
  ScreenoutOperation,
  ShutInOperation,
  SingleOperation,
  TripPipeAndCirculate
} from 'src/app/perical/models/thermal-operation.model';
import { FluidsService } from 'src/app/perical/services/fluids.service';
import { ThermalOperationsService } from 'src/app/perical/services/thermal-operations.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { WellConfigService } from 'src/app/shared/services/well-config.service';
import { Perforation } from 'src/app/perical/models/perforation.model';
import { UdtService } from 'src/app/shared/services/udt.service';
import { CurrentAppIds, User } from 'src/app/core/components/user-admin-page/user-model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { Reservoir } from 'src/app/perical/models/reservoirPerformance';
import { Choke } from 'src/app/perical/models/chokes.model';
import { ReservoirService } from 'src/app/perical/services/reservoir.service';
import { ChokesService } from 'src/app/perical/services/chokes.service';
import { PericalOptions } from 'src/app/perical/models/pericalOptions';
import { SessionStorageKeys, StoreService } from 'src/app/core/services/store.service';
import { WellType } from 'src/app/shared/models/wellType.model';
import { DrillstringService } from 'src/app/wellbore-inputs/services/drillstring.service';
import { GetInchesFromValue, GetShortLengthValueFromInches } from 'src/app/perivis/shared/helpers/units.helper';

export interface SingleOperationForm {
  type: OperationType;
  productionOperation: ProductionOperation;
  injectionOperation: InjectionOperation;
  shutInOperation: ShutInOperation;
  circulationOperation: CirculationOperation;
  gasLiftOperation: GasLiftOperation;
  drillingOperation: DrillingOperation;
  cementingOperation: CementingOperation;
  runCasingAndCirculate: RunCasingAndCirculate;
  tripPipeAndCirculate: TripPipeAndCirculate;
  screenoutOperation: ScreenoutOperation;
}

@Component({
  selector: 'app-single-operation',
  templateUrl: './single-operation.component.html',
  providers: subformComponentProviders(SingleOperationComponent),
  standalone: false
})
export class SingleOperationComponent implements OnInit, OnDestroy, OnChanges {

  constructor(
    private _fluidsService: FluidsService,
    private _signalRService: SignalRService,
    private _wellConfigService: WellConfigService,
    private _thermalOperationsService: ThermalOperationsService,
    private _udtService: UdtService,
    private _reservoirService: ReservoirService,
    private _chokeService: ChokesService,
    private _storeService: StoreService,
    private _drillstringService: DrillstringService
  ) {
    this.setOperationTypes();
  }

  public operationTypes: any[];
  public OperationType: typeof OperationType = OperationType;
  public isLoading = signal<boolean>(false);
  public fluids$: Fluid[];
  public annularFluidId: string;
  public perforations: Perforation[];
  public surfaceTemperature: number;
  public currentString: any;
  public currentTubularIndex: number;
  public tubulars: any;
  public maxCircDepth: number;
  public reservoirs: Reservoir[];
  public chokes: Choke[];
  public pericalOptions: PericalOptions;
  public defaultHangerPressure: number;
  public drillString: any;
  public riser: any;
  public showLandingString: boolean;
  private _hasOilProperty = false;

  @Input()
  public isReadOnly: boolean;

  @Input()
  public user: User;

  @Input()
  public units: UserUnitsModel;

  @Input()
  public previousOperationMudPits: MudPit;

  @Input()
  public defaultScreenoutHangerPressure: number;

  @Input()
  public wellType: WellType;

  @Input()
  public triggerMudPitTempChange: boolean;

  @Input()
  public set hasOilProperty(value: boolean) {
    this._hasOilProperty = !!value;
    this.syncGasLiftOperationType();
  }

  public get hasOilProperty(): boolean {
    return this._hasOilProperty;
  }

  @Input() set typeInput(typeInput: any) {
    if (typeInput.source === 'annulus' && typeInput.type.value === 'circulation') {
      switch (this.tabType) {
        case 'inside': {
          this.form.formGroup.controls.type.setValue(OperationType.CIRCULATION, { emitEvent: false });
          break;
        }
        case 'annulus': {
          this.form.formGroup.controls.type.setValue(null, { emitEvent: false });
          break;
        }
      }
    } else if (typeInput.source === 'inside' && typeInput.type.value === 'circulation') {
      if (this.tabType === 'annulus') {
        this.form.formGroup.controls.type.setValue(null, { emitEvent: false });
      }
    } else if (!this.form.formGroup?.value.type && (typeInput.type?.value === 'production' || typeInput.type?.value === 'injection' || typeInput.type?.value === 'shutIn')) {
      if (typeInput.source === 'inside' && this.tabType === 'annulus') {
        this.form.formGroup.controls.type.setValue(OperationType.SHUTIN, { emitEvent: false });
      } else if (typeInput.source === 'annulus' && this.tabType === 'inside') {
        this.form.formGroup.controls.type.setValue(OperationType.SHUTIN, { emitEvent: false });
      }
    } else if (typeInput.type?.value === 'screenout') {
      if (typeInput.source === 'inside' && this.tabType === 'annulus') {
        this.form.formGroup.controls.type.setValue(OperationType.SCREENOUT, { emitEvent: false });
      } else if (typeInput.source === 'annulus' && this.tabType === 'inside') {
        this.form.formGroup.controls.type.setValue(OperationType.SCREENOUT, { emitEvent: false });
      }
    }
    
    this.syncGasLiftOperationType();
  }

  @Input() tabType: string;
  @Input() showScreenout: boolean;
  @Input() showRestartOperation: boolean;
  @Output() typeChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() drillingDepthRangeUpdate = new EventEmitter<{ startDepth: number, endDepth: number }>();
  @Output() fluidChanged = new EventEmitter<{ hasGasProperty: boolean, hasOilProperty: boolean, hasWaterMudProperty: boolean }>();
  @Output() selectedTabChange = new EventEmitter<string>();

  public form = createForm<SingleOperation, SingleOperationForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(null),
      productionOperation: new UntypedFormControl(null),
      injectionOperation: new UntypedFormControl(null),
      shutInOperation: new UntypedFormControl(null),
      circulationOperation: new UntypedFormControl(null),
      gasLiftOperation: new UntypedFormControl(null),
      drillingOperation: new UntypedFormControl(null),
      cementingOperation: new UntypedFormControl(null),
      runCasingAndCirculate: new UntypedFormControl(null),
      tripPipeAndCirculate: new UntypedFormControl(null),
      screenoutOperation: new UntypedFormControl(null)
    },
    toFormGroup: (obj: SingleOperation | null): SingleOperationForm | null => {
      if (!obj) {
        return null;
      }

      if (obj.type === 'drilling') {
        this.drillingDepthRangeUpdate.emit({ startDepth: obj['startDepth'], endDepth: obj['endDepth'] });
      }

      if (this.fluids$ && obj['fluid']) {
        const state = this.fluids$.find(x => x.id === obj['fluid']['id']).state;
        this.fluidChanged.emit({ hasGasProperty: state['gasComposition'] != undefined || state.type == 'co2Fluid' || state.type == 'vle',
          hasOilProperty: state['oilApiGravity'] > 0 || state.type == 'vle',
          hasWaterMudProperty: state['nominalDensity'] != undefined || state.type == 'blackOil' || state.type == 'vle' || state.type == 'cementSlurry' || state.type == 'polymer' });
      }

      this.syncGasLiftOperationType();

      return {
        productionOperation: obj.type === OperationType.PRODUCTION ? obj : null,
        injectionOperation: obj.type === OperationType.INJECTION ? obj : null,
        shutInOperation: obj.type === OperationType.SHUTIN ? obj : null,
        circulationOperation: obj.type === OperationType.CIRCULATION ? obj : null,
        gasLiftOperation: obj.type === OperationType.GASLIFT ? obj : null,
        drillingOperation: obj.type === OperationType.DRILLING ? obj : null,
        cementingOperation: obj.type === OperationType.CEMENTING ? obj : null,
        runCasingAndCirculate: obj.type === OperationType.RUNCASINGANDCIRCULATE ? obj : null,
        tripPipeAndCirculate: obj.type === OperationType.TRIPPIPEANDCIRCULATE ? obj : null,
        screenoutOperation: obj.type === OperationType.SCREENOUT ? obj : null,
        type: obj.type
      };
    },
    fromFormGroup: (formValue: SingleOperationForm): SingleOperation | null => {
      const { productionOperation, injectionOperation, shutInOperation, circulationOperation, gasLiftOperation, drillingOperation, cementingOperation, runCasingAndCirculate, tripPipeAndCirculate, screenoutOperation, type, ...commonValues } = formValue;

      switch (type) {
        case OperationType.PRODUCTION:
          return productionOperation ? { type, ...productionOperation, ...commonValues } : null;
        case OperationType.INJECTION:
          return injectionOperation ? { type, ...injectionOperation, ...commonValues } : null;
        case OperationType.SHUTIN:
          return shutInOperation ? { type, ...shutInOperation, ...commonValues } : null;
        case OperationType.CIRCULATION:
          return circulationOperation ? { type, ...circulationOperation, ...commonValues } : null;
        case OperationType.GASLIFT:
          return gasLiftOperation ? { type, ...gasLiftOperation, ...commonValues } : null;
        case OperationType.DRILLING:
          return drillingOperation ? { type, ...drillingOperation, ...commonValues } : null;
        case OperationType.CEMENTING:
          return cementingOperation ? { type, ...cementingOperation, ...commonValues } : null;
        case OperationType.RUNCASINGANDCIRCULATE:
          return runCasingAndCirculate ? { type, ...runCasingAndCirculate, ...commonValues } : null;
        case OperationType.TRIPPIPEANDCIRCULATE:
          return tripPipeAndCirculate ? { type, ...tripPipeAndCirculate, ...commonValues } : null;
        case OperationType.SCREENOUT:
          return screenoutOperation ? { type, ...screenoutOperation, ...commonValues } : null;
        case null:
          return null;
        default:
      }
    }
  });

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);

    this.filterOperations();
      
    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  private signalRfunc(data: any) {
    if (data.action === PeriforOnChangeMessages.REFRESH_FLUIDS) {
      this._fluidsService.getFluids().subscribe(fluids => {
        this.fluids$ = fluids;
      });
    }
    if (data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS ||
      data.action === PeriforOnChangeMessages.REFRESH_PERFORATIONS ||
      data.action === PeriforOnChangeMessages.REFRESH_PERICAL_OPTIONS ||
      data.action === PeriforOnChangeMessages.REFRESH_UDT_PLOT ||
      data.action === PeriforOnChangeMessages.REFRESH_DRILL_STRING ||
      data.action === PeriforOnChangeMessages.REFRESH_RISERS) {
      this.filterOperations();
    }
    if (data.action == PeriforOnChangeMessages.REFRESH_RESERVOIR || data.action == PeriforOnChangeMessages.REFRESH_CHOKES) {
      const sources: Observable<any>[] = [
        this._reservoirService.getReservoirs() as Observable<any>,
        this._chokeService.getChokes() as Observable<any>
      ];

      forkJoin(sources).pipe(
        map(([reservoirs, chokes]) => {
          this.reservoirs = reservoirs.reservoirs;
          this.chokes = chokes.chokes;
        }),
        catchError(err => {
          return err;
        })).subscribe();
    }
  }

  private setOperationTypes() {
    this.operationTypes = [
      { name: 'Production', value: 'production' },
      { name: 'Injection', value: 'injection' },
      { name: 'Circulation', value: 'circulation' },
      { name: 'Trip Pipe & Circulate', value: 'tripPipeAndCirculate' },
      { name: 'Run Casing & Circulate', value: 'runCasingAndCirculate' },
      { name: 'Drilling', value: 'drilling' },
      { name: 'Cementing', value: 'cementing' },
      { name: 'Shut In', value: 'shutIn' },
      // { name: 'Screen-out (Fracturing/Injection)', value: 'screenout' }
    ];

    this.syncScreenoutOperationType();
  }

  private syncScreenoutOperationType(): void {
    const screenoutExists = this.operationTypes.some(x => x.value === OperationType.SCREENOUT);

    if (this.showScreenout) {
      if (!screenoutExists) {
        this.operationTypes = [
          ...this.operationTypes,
          { name: 'Screen-out (Fracturing/Injection)', value: OperationType.SCREENOUT }
        ];
      }
      return;
    }

    if (screenoutExists) {
      this.operationTypes = this.operationTypes.filter(x => x.value !== OperationType.SCREENOUT);
    }
  }

  private syncGasLiftOperationType(): void {
    if (this.tabType !== 'annulus') {
      return;
    }

    const gasLiftExists = this.operationTypes.some(x => x.value === OperationType.GASLIFT);

    if (this.hasOilProperty) {
      if (!gasLiftExists) {
        this.operationTypes = [ ...this.operationTypes, { name: 'Gas Lift', value: OperationType.GASLIFT } ];
      }
      return;
    }

    if (gasLiftExists) {
      this.operationTypes = this.operationTypes.filter(x => x.value !== OperationType.GASLIFT);
    }
  }

  onDrillingDepthRangeChange(depthRange) {
    this.drillingDepthRangeUpdate.emit({ startDepth: depthRange.startDepth, endDepth: depthRange.endDepth });
  }

  onFluidChange(fluidChange) {
    this.fluidChanged.emit({ hasGasProperty: fluidChange.hasGasProperty, hasOilProperty: fluidChange.hasOilProperty, hasWaterMudProperty: fluidChange.hasWaterMudProperty });
  }

  private filterOperations() {
    this.isLoading.set(true);
    this.setOperationTypes();
    this._wellConfigService.getTubulars().subscribe({
      next: (tubulars) => {
        if (tubulars.length > 0) {

      const sources: Observable<any>[] = [
        this._thermalOperationsService.getPerforations(),
        this._udtService.getUndisturbedTemperaturePoints(),
        this._fluidsService.getFluids() as Observable<any>,
        this._reservoirService.getReservoirs() as Observable<any>,
        this._chokeService.getChokes() as Observable<any>,
        this._thermalOperationsService.getPericalOptions() as Observable<any>,
        this._wellConfigService.getAnnulusContents(null) as Observable<any>,
        this._drillstringService.getDrillstringForTubular(),
        this._wellConfigService.getRiserForTubular()
      ];

      forkJoin(sources).pipe(
        map(([perforations, udt, fluids, reservoirs, chokes, pericalOptions, annulusContents, drillString, riser]) => {
          const appIds = this._storeService.getSession<CurrentAppIds>(SessionStorageKeys.APP_IDS);

          this.currentString = tubulars.find(x => x.id === appIds.tubularId);
          this.annularFluidId = this.currentString['annularFluidId'];

          this.drillString = drillString;

          if (riser?.riser?.outsideDiameter == null) {
            const pipeOD = this.currentString.stringSections?.[0]?.pipe?.outsideDiameter;
            const defaultRiserOD = GetInchesFromValue(pipeOD, this.units.shortLengths) >= 20 ? GetShortLengthValueFromInches(28, this.units.shortLengths) : GetShortLengthValueFromInches(21, this.units.shortLengths);
            const defaultRiserID = GetInchesFromValue(pipeOD, this.units.shortLengths) >= 20 ? GetShortLengthValueFromInches(26.5, this.units.shortLengths) : GetShortLengthValueFromInches(19.25, this.units.shortLengths);
            riser = {
              tubularStringId: this.currentString.id,
              designId: appIds.designId,
              includeRiser: this.wellType.type === 'Subsea' ? true : false,
              riser: {
                outsideDiameter: defaultRiserOD,
                insideDiameter: defaultRiserID,
                attachedTubularId: null
              }
            };
          }

          this.riser = riser;

          this.maxCircDepth = this.currentString.shoeMd;
          this.reservoirs = reservoirs?.reservoirs;
          this.chokes = chokes?.chokes;

          this.defaultHangerPressure = annulusContents.hangerPressure;

          this.pericalOptions = pericalOptions;
    
          this.fluids$ = fluids;
          this.tubulars = tubulars;
          this.showLandingString = this.wellType.type == 'Subsea' && this.currentString['hangerMd'] > 0;
          this.currentTubularIndex = tubulars.findIndex(x => x.id === this.currentString.id);
          const removeCount = tubulars.length - this.currentTubularIndex - 1;
          tubulars.splice(this.currentTubularIndex + 1, removeCount);
          const deepestShoe = Math.max(...tubulars.map(x => x.shoeMd));
          this.perforations = perforations.filter(x => x.measuredDepth <= deepestShoe).sort((a, b) => (a.measuredDepth < b.measuredDepth ? -1 : 1));

          this.surfaceTemperature = udt[0].temperature;
    
          if (this.currentString['hasCement']) {
            this.operationTypes = this.operationTypes.filter(x => x.value != 'circulation');
          } else {
            this.operationTypes = this.operationTypes.filter(x => x.value != 'cementing');
          }
    
          if (this.currentString['type'] == 'Tubing') {
            this.operationTypes = this.operationTypes.filter(x => x.value != 'drilling');
            this.operationTypes = this.operationTypes.filter(x => x.value != 'tripPipeAndCirculate');

            const runCasingIndex = this.operationTypes.findIndex(x => x.value == 'runCasingAndCirculate');
            if (runCasingIndex != -1) {
              this.operationTypes[runCasingIndex].name = 'Run Tubing & Circulate';
            }
          }
    
          if (this.currentString['type'] == 'Tieback') {
            this.operationTypes = this.operationTypes.filter(x => x.value != 'cementing');
          }

          this.syncGasLiftOperationType();
          this.isLoading.set(false);
        }),
        catchError(err => {
          this.isLoading.set(false);
          return err;
        })).subscribe();
      } else {
        this.isLoading.set(false); 
      }},
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['showScreenout']) {
      this.syncScreenoutOperationType();
    }

    if (changes['tabType']) {
      this.syncGasLiftOperationType();
    }
  }

  public onTypeChanged() {
    this.syncGasLiftOperationType();
    this.typeChanged.emit({
      type: this.form.formGroup.controls.type,
      source: this.tabType
    });
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }  
}
